import React from 'react';
import { X, CheckCircle2, XCircle, Loader2, MinusCircle, Circle } from 'lucide-react';
import { RemediationItemProgress } from '../../modules/VulnerabilityCenter/Utils/types';

interface RemediationProgressModalProps {
  show: boolean;
  onClose: () => void;
  items: RemediationItemProgress[];
  isComplete: boolean;
}

export const stageIcon = (stage: RemediationItemProgress['stage']) => {
  switch (stage) {
    case 'processing':
      return <Loader2 size={16} className="animate-spin text-red-500" />;
    case 'success':
      return <CheckCircle2 size={16} className="text-emerald-400" />;
    case 'failed':
      return <XCircle size={16} className="text-red-400" />;
    case 'skipped':
      return <MinusCircle size={16} className="text-amber-400" />;
    default:
      return <Circle size={16} className="text-gray-600" />;
  }
};

const RemediationProgressModal: React.FC<RemediationProgressModalProps> = ({ show, onClose, items, isComplete }) => {
  if (!show) return null;

  const total = items.length;
  const doneCount = items.filter((item) => item.stage === 'success' || item.stage === 'failed' || item.stage === 'skipped').length;
  const successCount = items.filter((item) => item.stage === 'success').length;
  const failedCount = items.filter((item) => item.stage === 'failed').length;
  const skippedCount = items.filter((item) => item.stage === 'skipped').length;
  const progressPercent = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity">
      <div className="bg-[#1c1c1c] text-white rounded-xl shadow-2xl p-6 w-[650px] max-h-[80vh] flex flex-col transform transition-all duration-300 scale-100 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-700 pb-3 mb-4">
          <h2 className="text-xl font-semibold">{isComplete ? 'Remediation Complete' : 'Creating Remediation Plans…'}</h2>
          {isComplete && (
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <X size={20} />
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>
              {doneCount} / {total} processed
            </span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                isComplete ? (failedCount === 0 ? 'bg-emerald-500' : 'bg-amber-500') : 'bg-red-600'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Stage-by-stage list */}
        <div className="overflow-y-auto flex-1 border border-gray-700 rounded-lg divide-y divide-gray-800 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          {items.map((item) => (
            <div key={item.key} className="flex items-center gap-3 px-4 py-2.5">
              <div className="flex-shrink-0">{stageIcon(item.stage)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{item.title}</div>
                {item.cveId && <div className="text-xs text-gray-500">{item.cveId}</div>}
              </div>
              <div className="text-xs text-gray-400 text-right flex-shrink-0 max-w-[240px]">{item.message || '—'}</div>
            </div>
          ))}
        </div>

        {/* Summary footer, shown once complete */}
        {isComplete && (
          <>
            <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
              <div className="rounded bg-zinc-800 p-2 text-gray-300 text-center">
                <div className="text-gray-400">Success</div>
                <div className="text-lg font-semibold text-emerald-400">{successCount}</div>
              </div>
              <div className="rounded bg-zinc-800 p-2 text-gray-300 text-center">
                <div className="text-gray-400">Skipped</div>
                <div className="text-lg font-semibold text-amber-400">{skippedCount}</div>
              </div>
              <div className="rounded bg-zinc-800 p-2 text-gray-300 text-center">
                <div className="text-gray-400">Failed</div>
                <div className="text-lg font-semibold text-red-400">{failedCount}</div>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button onClick={onClose} className="px-5 py-2 bg-red-700 hover:bg-red-800 rounded-full font-medium transition-all">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RemediationProgressModal;
